/**
 * Назначение: локальные буферы диагностики для кнопки ⚠ «Сообщить об ошибке».
 * Кто: staff SPA (все сотрудники).
 * Хранит только метаданные: НЕ тела запросов/ответов, НЕ заголовки, НЕ токены.
 */

export type ApiFailureDiagnostic = {
  method: string;
  path: string;
  status: number;
  code?: string;
  message: string;
  request_id?: string;
  ts: string;
  duration_ms: number;
};

export type UiErrorDiagnostic = {
  message: string;
  stack: string;
  source: 'console' | 'error' | 'unhandledrejection';
  ts: string;
};

const MAX_API_FAILURES = 20;
const MAX_UI_ERRORS = 20;
const MAX_ROUTES = 15;
const MAX_TEXT = 500;
const MAX_STACK_LINES = 10;

let apiFailures: ApiFailureDiagnostic[] = [];
let uiErrors: UiErrorDiagnostic[] = [];
let routes: string[] = [];
let installed = false;
let originalConsoleError: typeof console.error | null = null;

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const PHONE_RE = /(?:\+?\d[\d\s().-]{8,}\d)/g;

/** Маскирует email/телефоны и обрезает длинные строки. */
export function maskDiagnosticText(value: unknown): string {
  const raw = typeof value === 'string' ? value : String(value ?? '');
  const masked = raw
    .replace(EMAIL_RE, '<email>')
    .replace(PHONE_RE, '<phone>')
    .replace(/\s+/g, ' ')
    .trim();
  return masked.length > MAX_TEXT ? `${masked.slice(0, MAX_TEXT)}…` : masked;
}

/** Путь без значений query: остаются только имена параметров. */
export function sanitizePathForDiagnostics(path: string): string {
  const raw = String(path ?? '');
  const qIndex = raw.indexOf('?');
  if (qIndex < 0) return raw;
  const pathname = raw.slice(0, qIndex);
  const params = new URLSearchParams(raw.slice(qIndex + 1));
  const keys = Array.from(new Set(Array.from(params.keys())));
  if (!keys.length) return pathname;
  return `${pathname}?${keys.map((key) => `${encodeURIComponent(key)}=…`).join('&')}`;
}

export function recordApiFailure(input: {
  method: string;
  path: string;
  status: number;
  code?: string;
  message?: string;
  requestId?: string | null;
  durationMs?: number;
}): void {
  apiFailures.unshift({
    method: String(input.method || 'GET').toUpperCase(),
    path: sanitizePathForDiagnostics(input.path),
    status: Number(input.status) || 0,
    code: input.code ? maskDiagnosticText(input.code) : undefined,
    message: maskDiagnosticText(input.message ?? ''),
    request_id: input.requestId ? maskDiagnosticText(input.requestId) : undefined,
    ts: new Date().toISOString(),
    duration_ms: Math.max(0, Math.round(Number(input.durationMs) || 0))
  });
  if (apiFailures.length > MAX_API_FAILURES) apiFailures = apiFailures.slice(0, MAX_API_FAILURES);
}

export function recordUiError(message: unknown, stack?: unknown, source: UiErrorDiagnostic['source'] = 'error'): void {
  const stackText =
    typeof stack === 'string'
      ? stack
          .split('\n')
          .slice(0, MAX_STACK_LINES)
          .map((line) => maskDiagnosticText(line))
          .join('\n')
      : '';
  uiErrors.unshift({
    message: maskDiagnosticText(message),
    stack: stackText,
    source,
    ts: new Date().toISOString()
  });
  if (uiErrors.length > MAX_UI_ERRORS) uiErrors = uiErrors.slice(0, MAX_UI_ERRORS);
}

export function recordRoute(pathname: string): void {
  const path = sanitizePathForDiagnostics(pathname);
  if (!path || routes[0] === path) return;
  routes = [path, ...routes.filter((r) => r !== path)].slice(0, MAX_ROUTES);
}

export function getDiagnosticsSnapshot(): {
  api_failures: ApiFailureDiagnostic[];
  ui_errors: UiErrorDiagnostic[];
  routes: string[];
} {
  return {
    api_failures: apiFailures.map((x) => ({ ...x })),
    ui_errors: uiErrors.map((x) => ({ ...x })),
    routes: [...routes]
  };
}

export function resetDiagnosticsForTests(): void {
  apiFailures = [];
  uiErrors = [];
  routes = [];
}

/** Ставит глобальные перехватчики (идемпотентно). */
export function installDiagnostics(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    recordUiError(event.message || 'window.onerror', event.error instanceof Error ? event.error.stack : '', 'error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    recordUiError(
      reason instanceof Error ? reason.message : reason,
      reason instanceof Error ? reason.stack : '',
      'unhandledrejection'
    );
  });

  originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    try {
      const text = args
        .map((arg) => (typeof arg === 'string' ? arg : Object.prototype.toString.call(arg)))
        .join(' ');
      recordUiError(text, '', 'console');
    } catch {
      /* диагностика не должна ломать логирование */
    }
    originalConsoleError?.apply(console, args);
  };
}

export function uninstallDiagnosticsForTests(): void {
  if (!installed) return;
  installed = false;
  if (originalConsoleError) {
    console.error = originalConsoleError;
    originalConsoleError = null;
  }
}
